/**
 * EdgingCurve - Canvas 2D per-frame edging angle curve for left & right boards.
 *
 * Plots two edge-angle series (left board, right board) against time on a
 * shared chart. The ideal edging zone (25–45°) is shaded as a semi-transparent
 * band. A vertical ice-blue line marks the current playback time. When the
 * `syncOffsetMs` exceeds a small threshold (problem turn), a shadow area is
 * drawn between the two curves and labeled "偏移 Xms".
 */
import { useEffect, useRef } from 'react';

export interface EdgingCurveProps {
  leftEdgeSeries: number[];
  rightEdgeSeries: number[];
  timestamps: number[];
  currentTimeMs: number;
  syncOffsetMs: number;
  width: number;
  height: number;
}

// Y axis range for edge angle (degrees).
const Y_MIN = 0;
const Y_MAX = 60;
// Ideal edging zone (degrees).
const IDEAL_MIN = 25;
const IDEAL_MAX = 45;
// Threshold above which a sync offset is flagged as a "problem turn".
const SYNC_PROBLEM_THRESHOLD_MS = 60;

/** Read a CSS variable from the document and return a fallback if missing. */
function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return value.trim() || fallback;
}

export default function EdgingCurve({
  leftEdgeSeries,
  rightEdgeSeries,
  timestamps,
  currentTimeMs,
  syncOffsetMs,
  width,
  height,
}: EdgingCurveProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    // Chart layout.
    const padL = 36;
    const padR = 12;
    const padT = 24;
    const padB = 28;
    const chartW = width - padL - padR;
    const chartH = height - padT - padB;

    const iceColor = readCssVar('--color-ice', '#22D3EE');
    const blueColor = readCssVar('--color-blue', '#3B82F6');
    const purpleColor = readCssVar('--color-purple', '#6366F1');
    const textColor = readCssVar('--text-secondary', '#B4B4C7');
    const tertiaryColor = readCssVar('--text-tertiary', '#6B6B80');

    // Empty state.
    if (
      !timestamps ||
      timestamps.length === 0 ||
      leftEdgeSeries.length === 0 ||
      rightEdgeSeries.length === 0
    ) {
      ctx.fillStyle = tertiaryColor;
      ctx.font = '13px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('暂无立刃数据', width / 2, height / 2);
      return;
    }

    const tMin = timestamps[0];
    const tMax = timestamps[timestamps.length - 1];
    const tRange = Math.max(1, tMax - tMin);

    const toX = (t: number) => padL + ((t - tMin) / tRange) * chartW;
    const toY = (v: number) =>
      padT + chartH - ((Math.min(Y_MAX, Math.max(Y_MIN, v)) - Y_MIN) / (Y_MAX - Y_MIN)) * chartH;

    // --- Ideal edging zone band (25-45°) ---
    const idealY1 = toY(IDEAL_MIN);
    const idealY2 = toY(IDEAL_MAX);
    ctx.fillStyle = 'rgba(34, 211, 238, 0.08)';
    ctx.fillRect(padL, idealY2, chartW, idealY1 - idealY2);
    // Dashed boundaries.
    ctx.strokeStyle = 'rgba(34, 211, 238, 0.25)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(padL, idealY1);
    ctx.lineTo(padL + chartW, idealY1);
    ctx.moveTo(padL, idealY2);
    ctx.lineTo(padL + chartW, idealY2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Zone label.
    ctx.fillStyle = 'rgba(34, 211, 238, 0.6)';
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('理想立刃区 25°-45°', padL + 4, (idealY1 + idealY2) / 2);

    // --- Grid + Y axis labels ---
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.fillStyle = tertiaryColor;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let v = 0; v <= 60; v += 15) {
      const y = toY(v);
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(padL + chartW, y);
      ctx.stroke();
      ctx.fillText(`${v}°`, padL - 4, y);
    }

    // --- Shadow area between curves (problem turn sync offset) ---
    const isProblem = Math.abs(syncOffsetMs) >= SYNC_PROBLEM_THRESHOLD_MS;
    if (isProblem) {
      const n = Math.min(leftEdgeSeries.length, rightEdgeSeries.length, timestamps.length);
      ctx.beginPath();
      ctx.moveTo(toX(timestamps[0]), toY(leftEdgeSeries[0]));
      for (let i = 1; i < n; i++) {
        ctx.lineTo(toX(timestamps[i]), toY(leftEdgeSeries[i]));
      }
      for (let i = n - 1; i >= 0; i--) {
        ctx.lineTo(toX(timestamps[i]), toY(rightEdgeSeries[i]));
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(239, 68, 68, 0.12)';
      ctx.fill();
    }

    // --- Left board curve ---
    const drawSeries = (series: number[], color: string) => {
      ctx.beginPath();
      for (let i = 0; i < series.length && i < timestamps.length; i++) {
        const x = toX(timestamps[i]);
        const y = toY(series[i]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.stroke();
    };
    drawSeries(leftEdgeSeries, blueColor);
    drawSeries(rightEdgeSeries, purpleColor);

    // --- Current time vertical line ---
    if (currentTimeMs >= tMin && currentTimeMs <= tMax) {
      const cx = toX(currentTimeMs);
      ctx.strokeStyle = iceColor;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(cx, padT);
      ctx.lineTo(cx, padT + chartH);
      ctx.stroke();
      ctx.setLineDash([]);
      // Small triangle marker at top.
      ctx.beginPath();
      ctx.moveTo(cx - 4, padT - 4);
      ctx.lineTo(cx + 4, padT - 4);
      ctx.lineTo(cx, padT);
      ctx.closePath();
      ctx.fillStyle = iceColor;
      ctx.fill();
    }

    // --- X axis baseline ---
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT + chartH);
    ctx.lineTo(padL + chartW, padT + chartH);
    ctx.stroke();

    // --- X axis time labels (start / current / end) ---
    ctx.fillStyle = tertiaryColor;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`${Math.round(tMin)}ms`, padL, padT + chartH + 4);
    ctx.textAlign = 'right';
    ctx.fillText(`${Math.round(tMax)}ms`, padL + chartW, padT + chartH + 4);

    // --- Legend (left/right board) ---
    ctx.font = '11px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const legendY = padT - 12;
    // Left board
    ctx.strokeStyle = blueColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padL, legendY);
    ctx.lineTo(padL + 16, legendY);
    ctx.stroke();
    ctx.fillStyle = textColor;
    ctx.fillText('左板', padL + 20, legendY);
    // Right board
    ctx.strokeStyle = purpleColor;
    ctx.beginPath();
    ctx.moveTo(padL + 64, legendY);
    ctx.lineTo(padL + 80, legendY);
    ctx.stroke();
    ctx.fillStyle = textColor;
    ctx.fillText('右板', padL + 84, legendY);

    // --- Sync offset label (problem turns) ---
    if (isProblem) {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.9)';
      ctx.font = '11px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(`偏移 ${Math.round(syncOffsetMs)}ms`, padL + chartW, legendY);
    }

    // --- Y axis label ---
    ctx.save();
    ctx.translate(10, padT + chartH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = tertiaryColor;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('立刃角 (°)', 0, 0);
    ctx.restore();
  }, [
    leftEdgeSeries,
    rightEdgeSeries,
    timestamps,
    currentTimeMs,
    syncOffsetMs,
    width,
    height,
  ]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block', width, height }}
      aria-label="立刃角度曲线"
    />
  );
}
